/**
 * assets/bundle-builder.js
 *
 * Reads ?collection=HANDLE from the URL, fetches that collection's products,
 * renders them as removable cards (sold-out items shown with a badge,
 * excluded from the cart), and adds whatever remains to the cart in one
 * batch request.
 */

class BundleBuilder {
  constructor(container) {
    this.container = container;
    this.loadingEl = container.querySelector('[data-bundle-loading]');
    this.emptyEl = container.querySelector('[data-bundle-empty]');
    this.grid = container.querySelector('[data-bundle-grid]');
    this.footer = container.querySelector('[data-bundle-footer]');
    this.addAllBtn = container.querySelector('[data-add-all]');
    this.totalEl = container.querySelector('[data-bundle-total]');

    this.currency = container.dataset.currency || 'USD';
    this.locale = container.dataset.locale || document.documentElement.lang || 'en';

    // variantId -> { variantId, quantity, price, title } — in-stock items only
    this.selected = new Map();
    // Total cards rendered (in-stock + sold-out), used to decide empty state
    this.renderedCount = 0;

    this.init();
  }

  async init() {
    const handle = new URLSearchParams(window.location.search).get('collection');

    if (!handle) {
      this.showMessage('No collection specified. Go back and try again.');
      return;
    }

    try {
      const products = await this.fetchCollectionProducts(handle);

      if (!products.length) {
        this.showMessage('This collection has no products.');
        return;
      }

      this.renderProducts(products);
      this.bindEvents();
      this.loadingEl.hidden = true;
      // Visibility of grid/footer/empty state is handled by updateFooter(),
      // called at the end of renderProducts.
    } catch (err) {
      console.error('Bundle builder error:', err);
      this.showMessage('Could not load products. Please try again.');
    }
  }

  showMessage(message) {
    this.loadingEl.hidden = true;
    this.emptyEl.hidden = false;
    this.emptyEl.querySelector('p').textContent = message;
  }

  async fetchCollectionProducts(handle, allProducts = [], page = 1) {
    // Shopify's /products.json caps at 250 per page; loop if collection is larger.
    const res = await fetch(`/collections/${handle}/products.json?limit=250&page=${page}`);
    if (!res.ok) throw new Error(`Failed to fetch collection: ${handle}`);

    const data = await res.json();
    const products = data.products || [];
    const combined = allProducts.concat(products);

    if (products.length === 250) {
      return this.fetchCollectionProducts(handle, combined, page + 1);
    }

    return combined;
  }

  renderProducts(products) {
    const inStockFragment = document.createDocumentFragment();
    const soldOutFragment = document.createDocumentFragment();
    let renderedCount = 0;

    products.forEach((product) => {
      // Prefer an available variant. If none are available, fall back to
      // the first variant purely for display — it's shown as sold out and
      // is never added to `selected`, so it can't reach the cart.
      const availableVariant = product.variants.find((v) => v.available);
      const variant = availableVariant || product.variants[0];
      if (!variant) return; // product has no variants at all — nothing to show

      const isSoldOut = !availableVariant;
      renderedCount++;

      if (!isSoldOut) {
        this.selected.set(variant.id, {
          variantId: variant.id,
          quantity: 1,
          price: parseFloat(variant.price), // products.json returns a decimal string, e.g. "135.00" — not cents
          title: product.title,
        });
      }

      const image = variant.featured_image?.src || product.images[0]?.src || '';

      const card = document.createElement('div');
      card.className = 'bundle-item' + (isSoldOut ? ' bundle-item--sold-out' : '');
      card.dataset.variantId = variant.id;
      if (isSoldOut) card.dataset.soldOut = 'true';

      card.innerHTML = `
        <button type="button" class="bundle-item__remove" data-remove aria-label="Remove ${this.escapeHtml(product.title)}">&times;</button>
        ${isSoldOut ? '<span class="bundle-item__badge">Sold Out</span>' : ''}
        ${image ? `<img src="${image}" alt="${this.escapeHtml(product.title)}" width="180" height="180" loading="lazy">` : ''}
        <p class="bundle-item__title">${this.escapeHtml(product.title)}</p>
        <p class="bundle-item__price">${this.formatMoney(parseFloat(variant.price))}</p>
      `;

      // In-stock items go in the first fragment, sold-out in the second —
      // appending in-stock, then sold-out, keeps sold-out cards at the bottom.
      (isSoldOut ? soldOutFragment : inStockFragment).appendChild(card);
    });

    this.grid.appendChild(inStockFragment);
    this.grid.appendChild(soldOutFragment);
    this.renderedCount = renderedCount;
    this.updateFooter();
  }

  bindEvents() {
    this.grid.addEventListener('click', (e) => {
      const removeBtn = e.target.closest('[data-remove]');
      if (!removeBtn) return;

      const card = removeBtn.closest('.bundle-item');
      const variantId = Number(card.dataset.variantId);

      // No-op for sold-out cards (they were never added), harmless either way
      this.selected.delete(variantId);
      this.renderedCount = Math.max(0, this.renderedCount - 1);

      card.classList.add('is-removing');
      card.addEventListener('transitionend', () => card.remove(), { once: true });

      this.updateFooter();
    });

    this.addAllBtn.addEventListener('click', () => this.addAllToCart());
  }

  updateFooter() {
    // Nothing rendered at all (every product removed, or none had variants)
    if (this.renderedCount === 0) {
      this.footer.hidden = true;
      this.grid.hidden = true;
      this.emptyEl.hidden = false;
      return;
    }

    this.grid.hidden = false;
    this.footer.hidden = false;
    this.emptyEl.hidden = true;

    const count = this.selected.size;
    const total = Array.from(this.selected.values()).reduce(
      (sum, item) => sum + item.price * item.quantity,
      0
    );

    this.totalEl.textContent = this.formatMoney(total);

    if (count === 0) {
      // Everything left on the page is sold out — nothing to add
      this.addAllBtn.textContent = 'No items available';
      this.addAllBtn.disabled = true;
    } else {
      this.addAllBtn.textContent = `Add All to Cart (${count})`;
      this.addAllBtn.disabled = false;
    }
  }

  async addAllToCart() {
    if (this.selected.size === 0) return;

    this.addAllBtn.disabled = true;
    this.addAllBtn.textContent = 'Adding...';

    const items = Array.from(this.selected.values()).map((item) => ({
      id: item.variantId,
      quantity: item.quantity,
    }));

    try {
      const res = await fetch('/cart/add.js', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ items }),
      });

      if (!res.ok) {
        const err = await res.json();
        throw new Error(err.description || 'One or more items could not be added.');
      }

      await this.refreshCartDrawer();
    } catch (err) {
      alert(err.message);
      this.addAllBtn.disabled = false;
      this.addAllBtn.textContent = `Add All to Cart (${this.selected.size})`;
    }
  }

  async refreshCartDrawer() {
    const { CartUpdateEvent } = await import('@theme/events');
    const cart = await fetch('/cart.js').then((res) => res.json());

    const event = new CartUpdateEvent(cart, 'manual-trigger', {
      itemCount: cart.item_count,
      source: 'bundle-builder-refresh',
      sections: {},
    });
    document.dispatchEvent(event);

    const cartDrawer = document.querySelector('cart-drawer-component');
    if (cartDrawer?.hasAttribute('auto-open')) {
      cartDrawer.open();
    }

    // Always reset the button — regardless of whether the drawer opened —
    // and show the live total item count now sitting in the cart.
    this.addAllBtn.disabled = false;
    this.addAllBtn.textContent = `Add All to Cart (${cart.item_count} in cart)`;
  }

  formatMoney(amount) {
    try {
      return new Intl.NumberFormat(this.locale, {
        style: 'currency',
        currency: this.currency,
      }).format(amount);
    } catch (err) {
      // Fallback if currency code is missing/invalid
      return amount.toFixed(2) + ' ' + this.currency;
    }
  }

  escapeHtml(str) {
    const div = document.createElement('div');
    div.textContent = str;
    return div.innerHTML;
  }
}

document.addEventListener('DOMContentLoaded', () => {
  document.querySelectorAll('[data-bundle-builder]').forEach((el) => new BundleBuilder(el));
});